import React from 'react';
import { BeltColor, getBeltColor } from '@/constants/beltColors';

interface ProgressBeltProps {
  beltColor: BeltColor;
  progress: number;
  total: number;
  weeklyGoals: boolean[];
  className?: string;
}

export const ProgressBelt: React.FC<ProgressBeltProps> = ({
  beltColor,
  progress,
  total,
  weeklyGoals,
  className = ''
}) => {
  // Belt color mapping to SVG files
  const getBeltSvgPath = (color: BeltColor) => {
    switch (color) {
      case 'white':
        return new URL('/src/assets/progress/white_belt_base.svg', import.meta.url).href;
      case 'blue':
        return new URL('/src/assets/progress/blue_belt_base.svg', import.meta.url).href;
      case 'purple':
        return new URL('/src/assets/progress/purple_belt_base.svg', import.meta.url).href;
      case 'brown':
        return new URL('/src/assets/progress/brown_belt_base.svg', import.meta.url).href;
      case 'black':
        return new URL('/src/assets/progress/black_belt_base.svg', import.meta.url).href;
      default:
        return new URL('/src/assets/progress/white_belt_base.svg', import.meta.url).href;
    }
  };

  // Calculate progress percentage
  const progressPercentage = Math.min((progress / total) * 100, 100);
  
  return (
    <div className={`relative w-full ${className}`}>
      {/* SVG de fundo com barra transparente e stripes */}
      <img 
        src={getBeltSvgPath(beltColor)}
        alt={`${beltColor} belt background`}
        className="w-full h-auto"
        style={{
          filter: `drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25))`
        }}
      />
      
      {/* Barra de progresso horizontal sobre a barra transparente do fundo */}
      <div 
        className="absolute top-0 left-0 h-full pointer-events-none"
        style={{
          width: `${Math.max(progressPercentage, 1)}%`, // Mínimo de 1% para sempre ser visível
          overflow: 'hidden'
        }}
      >
        {/* Barra de progresso horizontal preenchida - sobre a barra transparente do fundo */}
        <div 
          className="absolute top-[0.8965px] left-[1px] h-[18px] rounded-sm shadow-lg"
          style={{
            backgroundColor: getBeltColor(beltColor), // Cor sólida da faixa
            width: '316.207px', // Largura total da barra horizontal do SVG
            transform: 'matrix(-0.999994, 0.00333513, -0.0103204, -0.999947, 0, 0)',
            transformOrigin: 'left center'
          }}
        />
      </div>
      
      {/* Debug: Barra sempre visível para teste - agora com a cor da faixa */}
      <div 
        className="absolute top-[0.8965px] left-[1px] h-[18px] rounded-sm z-10"
        style={{
          backgroundColor: getBeltColor(beltColor), // Cor da faixa em vez de vermelho
          width: `${progressPercentage}%`,
          minWidth: '10px' // Mínimo para sempre ser visível
        }}
      />
      
      {/* Stripes (graus) de progresso semanal renderizados por cima */}
      <div className="absolute top-0 left-0 h-full pointer-events-none">
        {weeklyGoals.map((completed, index) => {
          // Inverter a ordem: da direita para a esquerda
          const visualIndex = 3 - index; // 3, 2, 1, 0 em vez de 0, 1, 2, 3
          return (
            <div
              key={index}
              className="absolute h-4 shadow-lg"
              style={{
                backgroundColor: completed ? 'white' : 'transparent',
                width: '4px',
                top: `${(visualIndex * 1.5) + 25 - 4 + 2 + 1}px`, // +1px para baixo
                left: `${(56 + visualIndex * 8) + 50 + 16 - 2}px`, // Distância reduzida entre graus (8px em vez de 10px)
                transform: 'rotate(-169.965deg)',
                transformOrigin: 'left center',
                border: completed ? 'none' : '2px dashed white',
                opacity: completed ? 1 : 0.3
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
